import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, Euro, ChevronLeft, AlertTriangle, CheckCircle, TrendingUp,
  Ruler, Hammer, Save, Calculator, FolderOpen, Trash2, Eye, Bot,
  Search, Sparkles, FileDown, Clock, Landmark, PiggyBank, Info, Loader2
} from 'lucide-react';

const MDBPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const [inputs, setInputs] = useState({
    nom_projet: '',
    localisation: '',
    prix_vente_estime: 500000,
    prix_acquisition: 0, // NOUVEAU: Prix d'achat envisagé (0 = sera suggéré)
    surface_m2: 150,
    type_renovation: 'renovation',
    cout_m2_travaux: 0, // 0 = use preset
    profit_minimum_pct: 18,
    profit_minimum_custom: '',
    risque_travaux_pct: 4,
    risque_travaux_custom: '',
    // Paramètres de financement personnalisables (0 = sera calculé automatiquement)
    apport: 0,
    montant_emprunte: 0,
    taux_interet_mdb: 0,
    frais_courtage: 0,
    frais_dossier: 0,
    frais_etude_banque: 0,
    mois_commercialisation: 0,
    // Frais personnalisables (pourcentages)
    frais_notaire_pct: 2.5, // 2.5% par défaut
    frais_agence_revente_pct: 5, // 5% par défaut
    pas_de_frais_agence: false, // true = pas d'agence
    // Mode paiement
    paiement_cash: false,
  });

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const resp = await axios.get(`${API}/mdb/projects`, { withCredentials: true });
      setProjects(resp.data);
    } catch (e) { console.error(e); }
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setSelectedProjectId(null);
  };

  const loadProject = (project) => {
    setInputs({
      nom_projet: project.nom_projet || '',
      localisation: project.localisation || '',
      prix_vente_estime: project.prix_vente_estime || 500000,
      prix_acquisition: project.prix_acquisition || 0,
      surface_m2: project.surface_m2 || 150,
      type_renovation: project.type_renovation || 'renovation',
      cout_m2_travaux: project.cout_m2_travaux || 0,
      profit_minimum_pct: project.profit_minimum_pct || 18,
      profit_minimum_custom: '',
      risque_travaux_pct: project.risque_travaux_pct || 4,
      risque_travaux_custom: '',
      apport: project.apport || 0,
      montant_emprunte: project.montant_emprunte_input || 0,
      taux_interet_mdb: project.taux_interet_mdb || 0,
      frais_courtage: project.frais_courtage_input || 0,
      frais_dossier: project.frais_dossier_input || 0,
      frais_etude_banque: project.frais_etude_banque_input || 0,
      mois_commercialisation: project.mois_commercialisation || 0,
      frais_agence_revente: project.frais_agence_revente || 0,
      frais_notaire_custom: project.frais_notaire_custom || 0,
      frais_notaire_pct: project.frais_notaire_pct ?? 2.5,
      frais_agence_revente_pct: project.frais_agence_revente_pct ?? 5,
      pas_de_frais_agence: project.pas_de_frais_agence || false,
      paiement_cash: project.paiement_cash || false,
    });
    setResults({
      offre_maximum: project.offre_maximum,
      offre_maximum_theorique: project.offre_maximum_theorique,
      cout_travaux: project.cout_travaux,
      frais_notaire: project.frais_notaire,
      frais_vente: project.frais_vente,
      frais_caches: project.frais_caches,
      frais_detention: project.frais_detention,
      frais_emprunt: project.frais_emprunt,
      profit_estime: project.profit_estime,
      rentabilite: project.rentabilite,
      prix_m2_revente: project.prix_m2_revente,
      montant_emprunte: project.montant_emprunte,
      apport_calcule: project.apport_calcule,
      duree_detention_mois: project.duree_detention_mois,
      est_rentable: project.est_rentable,
      ecart_offre: project.ecart_offre,
      recommandations: project.recommandations || [],
      points_vigilance: project.points_vigilance || [],
    });
    setSelectedProjectId(project.project_id);
    toast.success(`Projet "${project.nom_projet}" chargé`);
  };

  const newProject = () => {
    setInputs({
      nom_projet: '', localisation: '', prix_vente_estime: 500000, prix_acquisition: 0,
      surface_m2: 150, type_renovation: 'renovation', cout_m2_travaux: 0, profit_minimum_pct: 18,
      profit_minimum_custom: '', risque_travaux_pct: 4, risque_travaux_custom: '',
      apport: 0, montant_emprunte: 0, taux_interet_mdb: 0, frais_courtage: 0,
      frais_dossier: 0, frais_etude_banque: 0, mois_commercialisation: 0,
      frais_agence_revente: 0, frais_notaire_custom: 0,
      frais_notaire_pct: 2.5, frais_agence_revente_pct: 5, pas_de_frais_agence: false,
      paiement_cash: false,
    });
    setResults(null);
    setSelectedProjectId(null);
  };

  const calculateMDB = async () => {
    setLoading(true);
    try {
      const payload = { ...inputs };
      if (inputs.profit_minimum_pct === -1 && inputs.profit_minimum_custom) {
        payload.profit_minimum_pct = parseFloat(inputs.profit_minimum_custom) || 18;
      }
      if (inputs.risque_travaux_pct === -1 && inputs.risque_travaux_custom) {
        payload.risque_travaux_pct = parseFloat(inputs.risque_travaux_custom) || 4;
      }
      const resp = await axios.post(`${API}/mdb/calculate`, payload, { withCredentials: true });
      setResults(resp.data);
      toast.success('Calcul effectue !');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Erreur lors du calcul';
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du calcul');
      console.error('MDB calc error:', error.response?.data || error.message);
    } finally { setLoading(false); }
  };

  const saveProject = async () => {
    if (!inputs.nom_projet) { toast.error('Veuillez nommer votre projet'); return; }
    try {
      await axios.post(`${API}/mdb/projects`, { ...inputs, ...results }, { withCredentials: true });
      toast.success('Projet sauvegard\u00e9 !');
      fetchProjects();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
  };

  const deleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await axios.delete(`${API}/mdb/projects/${projectId}`, { withCredentials: true });
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
      if (selectedProjectId === projectId) { setSelectedProjectId(null); }
      toast.success('Projet supprim\u00e9');
    } catch { toast.error('Erreur'); }
  };

  const downloadPDF = async () => {
    if (!results) return;
    setPdfLoading(true);
    try {
      const resp = await axios.post(`${API}/mdb/pdf`, { ...inputs, ...results },
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilan_mdb_${inputs.nom_projet || 'projet'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF t\u00e9l\u00e9charg\u00e9 !');
    } catch { toast.error('Erreur PDF'); }
    finally { setPdfLoading(false); }
  };

  const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

  const renovationTypes = [
    { value: 'sans_travaux', label: 'Sans travaux', cost: 0, description: 'Aucun travaux pr\u00e9vu' },
    { value: 'rafraichissement', label: 'Rafra\u00eechissement', cost: 300, description: 'Peinture, nettoyage, d\u00e9coration' },
    { value: 'renovation', label: 'R\u00e9novation', cost: 600, description: 'Fen\u00eatres, \u00e9lectricit\u00e9, plomberie' },
    { value: 'reagencement', label: 'R\u00e9agencement', cost: 900, description: 'R\u00e9novation + cr\u00e9ation pi\u00e8ces' },
    { value: 'gros_oeuvre', label: 'Gros \u0153uvre', cost: 1200, description: 'Permis, murs porteurs, structure' }
  ];

  const selectedReno = renovationTypes.find(t => t.value === inputs.type_renovation);
  const effectiveCostM2 = inputs.cout_m2_travaux > 0 ? inputs.cout_m2_travaux : (selectedReno?.cost ?? 600);
  const effectiveProfitPct = inputs.profit_minimum_pct === -1 ? (parseFloat(inputs.profit_minimum_custom) || 18) : inputs.profit_minimum_pct;
  const effectiveRisquePct = inputs.risque_travaux_pct === -1 ? (parseFloat(inputs.risque_travaux_custom) || 4) : inputs.risque_travaux_pct;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12 sm:h-16 gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5 px-2 sm:px-3 flex-shrink-0">
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Dashboard</span>
                </Button>
              </Link>
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-[#00B050] flex-shrink-0" />
                <h1 className="font-semibold text-slate-900 text-sm sm:text-base truncate">MDB</h1>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={newProject} className="text-xs sm:text-sm px-2 sm:px-3" data-testid="new-project-btn">
                <Trash2 className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Nouveau</span>
              </Button>
              {results && (
                <>
                  <Button size="sm" variant="outline" className="border-[#0D47A1] text-[#0D47A1] text-xs sm:text-sm px-2 sm:px-3"
                    onClick={downloadPDF} disabled={pdfLoading} data-testid="download-mdb-pdf-btn">
                    <FileDown className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">PDF</span>
                  </Button>
                  <Button size="sm" className="bg-[#00B050] hover:bg-[#00853C] text-xs sm:text-sm px-2 sm:px-3"
                    onClick={saveProject} data-testid="save-mdb-btn">
                    <Save className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Sauver</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 overflow-x-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8">
          {/* Input Form */}
          <div className="lg:col-span-7 space-y-4 sm:space-y-6">
            {/* Project Info */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3 sm:pb-6"><CardTitle className="flex items-center text-sm sm:text-base"><Building2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[#00B050]" />Informations du projet</CardTitle></CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs sm:text-sm">Nom du projet</Label>
                    <Input placeholder="Ex: Immeuble Rue de la Paix" value={inputs.nom_projet}
                      onChange={(e) => handleInputChange('nom_projet', e.target.value)} data-testid="input-nom-projet" />
                  </div>
                  <div>
                    <Label className="text-xs sm:text-sm">Localisation</Label>
                    <Input placeholder="Ex: Lyon, Bordeaux..." value={inputs.localisation}
                      onChange={(e) => handleInputChange('localisation', e.target.value)} data-testid="input-localisation" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs sm:text-sm">Prix de vente estime</Label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input type="number" value={inputs.prix_vente_estime}
                        onChange={(e) => handleInputChange('prix_vente_estime', parseFloat(e.target.value) || 0)}
                        className="pl-10" data-testid="input-prix-vente" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Prix auquel vous pensez revendre le bien</p>
                  </div>
                  <div>
                    <Label className="text-xs sm:text-sm">Surface (m2)</Label>
                    <div className="relative">
                      <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input type="number" value={inputs.surface_m2}
                        onChange={(e) => handleInputChange('surface_m2', parseFloat(e.target.value) || 0)}
                        className="pl-10" data-testid="input-surface" />
                    </div>
                  </div>
                </div>
                {/* NOUVEAU: Prix d'acquisition envisagé */}
                <div className="p-4 rounded-lg border-2 border-[#0D47A1] bg-[#0D47A1]/5">
                  <Label className="text-[#0D47A1] font-semibold">Prix d'acquisition envisag&eacute;</Label>
                  <p className="text-xs text-slate-500 mb-2">Le prix d'achat que vous comptez proposer</p>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0D47A1]" />
                    <Input type="number" value={inputs.prix_acquisition || ''}
                      placeholder="Ex: 370000"
                      onChange={(e) => handleInputChange('prix_acquisition', parseFloat(e.target.value) || 0)}
                      className="pl-10 border-[#0D47A1] focus:ring-[#0D47A1]" data-testid="input-prix-acquisition" />
                  </div>
                  {inputs.prix_acquisition > 0 && inputs.surface_m2 > 0 && (
                    <p className="text-xs text-[#0D47A1] mt-2 font-medium">
                      soit {fmt(inputs.prix_acquisition / inputs.surface_m2)} / m&sup2;
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Frais de notaire & Frais d'agence revente */}
            <Card className="border-slate-200">
              <CardHeader className="pb-3 sm:pb-6"><CardTitle className="flex items-center text-sm sm:text-base"><Landmark className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[#00B050]" />Frais de notaire & Agence revente</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Frais de notaire */}
                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Frais de notaire (%)</Label>
                  <p className="text-xs text-slate-500 mb-2">Par défaut 2.5%. Modifiez le pourcentage si nécessaire.</p>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="number" 
                      step="0.1"
                      min="0"
                      value={inputs.frais_notaire_pct}
                      onChange={(e) => handleInputChange('frais_notaire_pct', parseFloat(e.target.value) || 0)}
                      className="max-w-[120px] text-center font-semibold"
                      data-testid="input-frais-notaire-pct" 
                    />
                    <span className="text-slate-500 font-medium">%</span>
                    {inputs.prix_acquisition > 0 && (
                      <span className="text-sm text-[#0D47A1] font-medium">
                        = {fmt(inputs.prix_acquisition * inputs.frais_notaire_pct / 100)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Frais d'agence revente */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs sm:text-sm font-semibold">Frais d'agence pour la revente (%)</Label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={inputs.pas_de_frais_agence}
                        onChange={(e) => handleInputChange('pas_de_frais_agence', e.target.checked)}
                        className="rounded border-slate-300 text-[#00B050] focus:ring-[#00B050]"
                        data-testid="input-pas-de-frais-agence"
                      />
                      <span className={`text-xs sm:text-sm ${inputs.pas_de_frais_agence ? 'text-[#00B050] font-semibold' : 'text-slate-600'}`}>
                        Pas de frais d'agence
                      </span>
                    </label>
                  </div>
                  {!inputs.pas_de_frais_agence ? (
                    <>
                      <div className="flex items-center gap-3">
                        <Input 
                          type="number" 
                          step="0.1"
                          min="0"
                          value={inputs.frais_agence_revente_pct}
                          onChange={(e) => handleInputChange('frais_agence_revente_pct', parseFloat(e.target.value) || 0)}
                          className="max-w-[120px] text-center font-semibold"
                          data-testid="input-frais-agence-revente-pct" 
                        />
                        <span className="text-slate-500 font-medium">%</span>
                        {inputs.prix_vente_estime > 0 && (
                          <span className="text-sm text-[#0D47A1] font-medium">
                            = {fmt(inputs.prix_vente_estime * inputs.frais_agence_revente_pct / 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Pourcentage appliqué sur le prix de vente estimé</p>
                    </>
                  ) : (
                    <p className="text-sm text-[#00B050] font-medium">Vente directe sans agence — aucun frais</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Renovation Type + Manual Cost */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="flex items-center"><Hammer className="h-5 w-5 mr-2 text-[#00B050]" />Travaux</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-2 block">Type de r&eacute;novation (pr&eacute;s&eacute;lection)</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {renovationTypes.map((type) => (
                      <div key={type.value}
                        onClick={() => {
                          handleInputChange('type_renovation', type.value);
                          handleInputChange('cout_m2_travaux', 0); // Reset manual to use preset
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          inputs.type_renovation === type.value && inputs.cout_m2_travaux === 0
                            ? 'border-[#00B050] bg-[#00B050]/5' : 'border-slate-200 hover:border-slate-300'
                        }`} data-testid={`renovation-${type.value}`}>
                        <div className="font-semibold text-slate-900 text-sm">{type.label}</div>
                        <div className="text-sm text-[#00B050] font-mono">~{type.cost}&euro;/m&sup2;</div>
                        <div className="text-xs text-slate-500">{type.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`p-4 rounded-lg border-2 ${inputs.cout_m2_travaux > 0 ? 'border-[#00B050] bg-[#00B050]/5' : 'border-dashed border-slate-300'}`}>
                  <Label className="text-sm font-semibold">Co&ucirc;t au m&sup2; manuel (prioritaire sur le pr&eacute;s&eacute;lection)</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input type="number" placeholder="Ex: 750" value={inputs.cout_m2_travaux || ''}
                      onChange={(e) => handleInputChange('cout_m2_travaux', parseFloat(e.target.value) || 0)}
                      className="max-w-[200px]" data-testid="input-cout-m2" />
                    <span className="text-slate-500">&euro;/m&sup2;</span>
                    {inputs.cout_m2_travaux > 0 && (
                      <span className="text-sm text-[#00B050] font-medium">
                        = {fmt(inputs.cout_m2_travaux * inputs.surface_m2)} de travaux
                      </span>
                    )}
                  </div>
                  {inputs.cout_m2_travaux > 0 && (
                    <button onClick={() => handleInputChange('cout_m2_travaux', 0)}
                      className="text-xs text-slate-400 hover:text-red-500 mt-1 underline">
                      Revenir au pr&eacute;s&eacute;lection ({selectedReno?.cost}&euro;/m&sup2;)
                    </button>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-2">
                  <Info className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <p className="text-xs text-slate-500">
                    Co&ucirc;t utilis&eacute; : <strong>{effectiveCostM2}&euro;/m&sup2;</strong> &times; {inputs.surface_m2}m&sup2; = <strong>{fmt(effectiveCostM2 * inputs.surface_m2)}</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Profit & Risk Settings */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="flex items-center"><TrendingUp className="h-5 w-5 mr-2 text-[#00B050]" />Objectifs de rentabilit&eacute;</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Profit minimum (%)</Label>
                    <Select value={inputs.profit_minimum_pct.toString()}
                      onValueChange={(v) => handleInputChange('profit_minimum_pct', parseFloat(v))}>
                      <SelectTrigger data-testid="select-profit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="15">15%</SelectItem>
                        <SelectItem value="18">18% (standard)</SelectItem>
                        <SelectItem value="20">20%</SelectItem>
                        <SelectItem value="25">25%</SelectItem>
                        <SelectItem value="30">30%</SelectItem>
                        <SelectItem value="-1">Manuel...</SelectItem>
                      </SelectContent>
                    </Select>
                    {inputs.profit_minimum_pct === -1 && (
                      <Input type="number" placeholder="Saisir %" value={inputs.profit_minimum_custom}
                        onChange={(e) => handleInputChange('profit_minimum_custom', e.target.value)}
                        className="mt-2" data-testid="input-profit-custom" />
                    )}
                  </div>
                  <div>
                    <Label>Risque travaux (%)</Label>
                    <Select value={inputs.risque_travaux_pct.toString()}
                      onValueChange={(v) => handleInputChange('risque_travaux_pct', parseFloat(v))}>
                      <SelectTrigger data-testid="select-risque"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="4">4% (standard)</SelectItem>
                        <SelectItem value="8">8%</SelectItem>
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="-1">Manuel...</SelectItem>
                      </SelectContent>
                    </Select>
                    {inputs.risque_travaux_pct === -1 && (
                      <Input type="number" placeholder="Saisir %" value={inputs.risque_travaux_custom}
                        onChange={(e) => handleInputChange('risque_travaux_custom', e.target.value)}
                        className="mt-2" data-testid="input-risque-custom" />
                    )}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">
                    Marge totale requise : <strong>{effectiveProfitPct + effectiveRisquePct}%</strong> (profit {effectiveProfitPct}% + risque {effectiveRisquePct}%)
                    = <strong>{fmt(inputs.prix_vente_estime * (effectiveProfitPct + effectiveRisquePct) / 100)}</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Paramètres de financement personnalisables */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="flex items-center text-sm sm:text-base"><Landmark className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-[#00B050]" />Financement</span>
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-normal cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={inputs.paiement_cash}
                      onChange={(e) => {
                        handleInputChange('paiement_cash', e.target.checked);
                        if (e.target.checked) {
                          // Reset financing fields when cash
                          handleInputChange('montant_emprunte', 0);
                          handleInputChange('taux_interet_mdb', 0);
                          handleInputChange('frais_courtage', 0);
                          handleInputChange('frais_dossier', 0);
                          handleInputChange('frais_etude_banque', 0);
                        }
                      }}
                      className="rounded border-slate-300 text-[#00B050] focus:ring-[#00B050]"
                      data-testid="input-paiement-cash"
                    />
                    <span className={inputs.paiement_cash ? 'text-[#00B050] font-semibold' : 'text-slate-600'}>
                      <PiggyBank className="h-4 w-4 inline mr-1" />Paiement Cash (sans emprunt)
                    </span>
                  </label>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Apport */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Apport</Label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input 
                        type="number" 
                        placeholder="0 €" 
                        value={inputs.apport || ''} 
                        onChange={(e) => handleInputChange('apport', parseFloat(e.target.value) || 0)}
                        className="pl-10"
                        data-testid="input-apport" 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Montant emprunt&eacute;</Label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input 
                        type="number" 
                        placeholder="0 €" 
                        value={inputs.montant_emprunte || ''} 
                        onChange={(e) => handleInputChange('montant_emprunte', parseFloat(e.target.value) || 0)}
                        className="pl-10"
                        disabled={inputs.paiement_cash}
                        data-testid="input-montant-emprunte" 
                      />
                    </div>
                  </div>
                </div>

                {/* Taux d'intérêt */}
                <div>
                  <Label>Taux d'int&eacute;r&ecirc;t annuel (%)</Label>
                  <Input 
                    type="number" 
                    step="0.1" 
                    placeholder="0 %" 
                    value={inputs.taux_interet_mdb || ''} 
                    onChange={(e) => handleInputChange('taux_interet_mdb', parseFloat(e.target.value) || 0)}
                    disabled={inputs.paiement_cash}
                    data-testid="input-taux-mdb" 
                  />
                </div>

                {/* Durée de détention */}
                <div>
                  <Label>Combien de temps souhaitez-vous garder ce bien avant de le revendre ? (mois)</Label>
                  <Input 
                    type="number" 
                    placeholder="0 mois" 
                    value={inputs.mois_commercialisation || ''} 
                    onChange={(e) => handleInputChange('mois_commercialisation', parseInt(e.target.value) || 0)}
                    data-testid="input-mois-commercialisation" 
                  />
                </div>

                {/* Frais bancaires */}
                {!inputs.paiement_cash && (
                  <div className="p-4 rounded-lg bg-slate-50 space-y-4">
                    <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Landmark className="h-4 w-4" />Frais bancaires
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Frais de courtage</Label>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input 
                            type="number" 
                            placeholder="0 €" 
                            value={inputs.frais_courtage || ''} 
                            onChange={(e) => handleInputChange('frais_courtage', parseFloat(e.target.value) || 0)}
                            className="pl-10 text-sm"
                            data-testid="input-frais-courtage" 
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Frais de dossier</Label>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input 
                            type="number" 
                            placeholder="0 €" 
                            value={inputs.frais_dossier || ''} 
                            onChange={(e) => handleInputChange('frais_dossier', parseFloat(e.target.value) || 0)}
                            className="pl-10 text-sm"
                            data-testid="input-frais-dossier" 
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Frais &eacute;tude banque</Label>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input 
                            type="number" 
                            placeholder="0 €" 
                            value={inputs.frais_etude_banque || ''} 
                            onChange={(e) => handleInputChange('frais_etude_banque', parseFloat(e.target.value) || 0)}
                            className="pl-10 text-sm"
                            data-testid="input-frais-etude-banque" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info récapitulative - uniquement pour mode cash */}
                {inputs.paiement_cash && (
                  <div className="rounded-lg p-3 bg-[#00B050]/10 border border-[#00B050]">
                    <p className="text-sm text-[#00B050] font-medium flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Mode CASH : Pas de frais d'emprunt (courtage, dossier, int&eacute;r&ecirc;ts = 0)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button onClick={calculateMDB} disabled={loading || !inputs.prix_acquisition}
              className="w-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90 h-11 sm:h-12 text-sm sm:text-lg font-bold" data-testid="calculate-mdb-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calcul en cours...</> : !inputs.prix_acquisition ? "Entrez un prix d'acquisition" : "Analyser l'operation"}
            </Button>
            {!inputs.prix_acquisition && (
              <p className="text-center text-sm text-amber-600">
                Veuillez entrer le prix d'acquisition envisag&eacute; pour analyser la rentabilit&eacute;
              </p>
            )}
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-5">
            <div className="sticky top-24 space-y-6">
              {results ? (
                <>
                  {/* Indicateur de rentabilité */}
                  <Card className={`border-2 shadow-lg ${results.est_rentable ? 'border-[#00B050] bg-[#00B050]/5' : 'border-red-500 bg-red-50'}`}>
                    <CardContent className="p-6 text-center">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${results.est_rentable ? 'bg-[#00B050]' : 'bg-red-500'}`}>
                        {results.est_rentable ? (
                          <CheckCircle className="h-8 w-8 text-white" />
                        ) : (
                          <AlertTriangle className="h-8 w-8 text-white" />
                        )}
                      </div>
                      <h2 className={`text-2xl font-bold mb-2 ${results.est_rentable ? 'text-[#00B050]' : 'text-red-600'}`}>
                        {results.est_rentable ? 'Opération RENTABLE' : 'Opération NON RENTABLE'}
                      </h2>
                      <p className="text-slate-600 text-sm">
                        Rentabilit&eacute;: <span className={`font-bold text-lg ${results.est_rentable ? 'text-[#00B050]' : 'text-red-600'}`}>{results.rentabilite?.toFixed(1)}%</span>
                        <span className="text-slate-400"> / minimum requis: {effectiveProfitPct + effectiveRisquePct}%</span>
                      </p>
                      {results.ecart_offre !== 0 && (
                        <p className={`text-sm mt-2 ${results.ecart_offre > 0 ? 'text-red-600' : 'text-[#00B050]'}`}>
                          {results.ecart_offre > 0 
                            ? `Prix trop élevé de ${fmt(results.ecart_offre)} par rapport au maximum`
                            : `Bonne affaire: ${fmt(Math.abs(results.ecart_offre))} sous le prix maximum`
                          }
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Résumé de l'opération */}
                  <Card className="border-slate-200">
                    <CardHeader className="bg-[#0D47A1] text-white">
                      <CardTitle className="text-lg">R&eacute;sum&eacute; de l'op&eacute;ration</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between py-2 border-b border-slate-100 bg-blue-50 -mx-2 px-2 rounded">
                          <span className="font-semibold text-[#0D47A1]">Prix d'acquisition envisag&eacute;</span>
                          <span className="font-mono font-bold text-[#0D47A1]">{fmt(inputs.prix_acquisition)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">Prix de vente estim&eacute;</span>
                          <span className="font-mono font-semibold">{fmt(inputs.prix_vente_estime)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">Co&ucirc;t travaux ({results.cout_m2_utilise}&euro;/m&sup2;)</span>
                          <span className="font-mono font-semibold text-red-500">-{fmt(results.cout_travaux)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">Frais de notaire ({results.frais_notaire_pct || 2.5}%)</span>
                          <span className="font-mono font-semibold text-red-500">-{fmt(results.frais_notaire)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">Frais d'emprunt</span>
                          <span className="font-mono font-semibold text-red-500">-{fmt(results.frais_emprunt)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">
                            {results.pas_de_frais_agence ? "Frais d'agence (aucun)" : `Frais d'agence revente (${results.frais_agence_revente_pct || 0}%)`}
                          </span>
                          <span className={`font-mono font-semibold ${results.frais_vente > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                            {results.frais_vente > 0 ? `-${fmt(results.frais_vente)}` : '0 €'}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">Frais de d&eacute;tention (2.5%)</span>
                          <span className="font-mono font-semibold text-red-500">-{fmt(results.frais_detention)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100 bg-slate-50 -mx-2 px-2 rounded">
                          <span className="font-semibold text-slate-700">Total frais cach&eacute;s</span>
                          <span className="font-mono font-semibold text-red-500">-{fmt(results.frais_caches)}</span>
                        </div>
                        <div className={`flex justify-between py-3 font-semibold text-lg ${results.profit_estime >= 0 ? 'text-[#00B050]' : 'text-red-600'}`}>
                          <span>PROFIT / PERTE</span>
                          <span className="font-mono">{results.profit_estime >= 0 ? '+' : ''}{fmt(results.profit_estime)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-600">Rentabilit&eacute;</span>
                          <span className={`font-mono font-bold ${results.est_rentable ? 'text-[#00B050]' : 'text-red-600'}`}>{results.rentabilite?.toFixed(2)}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Détail Financement */}
                  <Card className={`border-[#0D47A1] ${results.paiement_cash ? 'bg-[#00B050]/5' : ''}`}>
                    <CardHeader className="bg-[#0D47A1] text-white py-3">
                      <CardTitle className="text-sm flex items-center">
                        <Landmark className="h-4 w-4 mr-2" />
                        {results.paiement_cash ? 'Mode CASH (sans emprunt)' : 'D\u00e9tail Financement'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">{results.paiement_cash ? 'Apport total' : 'Apport'}</span>
                        <span className="font-mono font-semibold">{fmt(results.apport_calcule)}</span>
                      </div>
                      {!results.paiement_cash && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Montant emprunt&eacute;</span>
                            <span className="font-mono font-semibold">{fmt(results.montant_emprunte)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Taux d'int&eacute;r&ecirc;t</span>
                            <span className="font-mono">{results.taux_interet_utilise || inputs.taux_interet_mdb || 4.5}%</span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-400">
                            <span>Frais courtage</span>
                            <span className="font-mono">{fmt(results.frais_courtage)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-400">
                            <span>Frais dossier</span>
                            <span className="font-mono">{fmt(results.frais_dossier)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-400">
                            <span>Frais &eacute;tude banque</span>
                            <span className="font-mono">{fmt(results.frais_etude_banque)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Dur&eacute;e de d&eacute;tention</span>
                        <span className="font-mono">{results.duree_detention_mois?.toFixed(1)} mois</span>
                      </div>
                      {results.paiement_cash && (
                        <div className="pt-2 mt-2 border-t border-slate-200">
                          <p className="text-[#00B050] text-xs font-medium flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Économie: pas de frais d'emprunt
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Offre Maximum Théorique */}
                  <Card className="border-amber-300 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="h-5 w-5 text-amber-600" />
                        <span className="font-semibold text-amber-700">Offre Maximum Th&eacute;orique</span>
                      </div>
                      <p className="text-2xl font-bold text-amber-700 font-mono">{fmt(results.offre_maximum_theorique || results.offre_maximum)}</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Prix d'achat &agrave; ne pas d&eacute;passer pour garantir {effectiveProfitPct + effectiveRisquePct}% de rentabilit&eacute;
                      </p>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  {results.recommandations?.length > 0 && (
                    <Card className="border-slate-200">
                      <CardHeader><CardTitle className="text-sm flex items-center text-[#00B050]"><CheckCircle className="h-4 w-4 mr-2" />Recommandations</CardTitle></CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {results.recommandations.map((rec, i) => (
                            <li key={i} className="flex items-start text-sm text-slate-600">
                              <CheckCircle className="h-4 w-4 text-[#00B050] mr-2 flex-shrink-0 mt-0.5" />{rec}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Warnings */}
                  {results.points_vigilance?.length > 0 && (
                    <Card className="border-red-300 bg-red-50">
                      <CardHeader><CardTitle className="text-sm flex items-center text-red-700"><AlertTriangle className="h-4 w-4 mr-2" />Points de vigilance</CardTitle></CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {results.points_vigilance.map((p, i) => (
                            <li key={i} className="flex items-start text-sm text-red-700">
                              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />{p}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Search properties - Expert */}
                  {(user?.subscription_tier === 'club' || user?.subscription_tier === 'expert' || user?.subscription_tier === 'founder') && results.est_rentable && (
                    <Card className="border-[#0D47A1] bg-gradient-to-r from-[#0D47A1]/5 to-[#00B050]/5">
                      <CardContent className="p-6 text-center space-y-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#00B050] to-[#0D47A1] rounded-xl flex items-center justify-center mx-auto">
                          <Bot className="h-7 w-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#0D47A1] mb-1">Rechercher des biens similaires</h3>
                          <p className="text-sm text-slate-500">Agent IA pour trouver des biens &agrave; ce prix</p>
                        </div>
                        <Button onClick={() => {
                          const p = new URLSearchParams({
                            from: 'mdb', project_name: inputs.nom_projet || 'Projet MDB',
                            localisation: inputs.localisation || 'France',
                            budget_max: Math.round(inputs.prix_acquisition).toString(),
                            surface_min: Math.round(inputs.surface_m2 * 0.8).toString(), rendement_min: '5'
                          });
                          navigate(`/ai-agent?${p.toString()}`);
                        }} className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90" data-testid="search-properties-btn">
                          <Search className="h-4 w-4 mr-2" />Rechercher des biens<Sparkles className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="border-slate-200">
                  <CardContent className="p-8 text-center">
                    <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Analysez votre op&eacute;ration MDB</h3>
                    <p className="text-slate-500">Renseignez les informations pour calculer le prix d'achat maximum.</p>
                  </CardContent>
                </Card>
              )}

              {/* Projects list */}
              {projects.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-sm flex items-center"><FolderOpen className="h-4 w-4 mr-2" />Mes projets</CardTitle>
                    <Button variant="ghost" size="sm" onClick={newProject} data-testid="new-project-btn">+ Nouveau</Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {projects.map((project, idx) => (
                        <div key={idx}
                          className={`p-3 rounded-lg text-sm cursor-pointer transition-all group hover:shadow-md ${
                            selectedProjectId === project.project_id
                              ? 'bg-[#00B050]/10 border-2 border-[#00B050]' : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                          }`}
                          onClick={() => loadProject(project)} data-testid={`load-project-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-slate-900">{project.nom_projet || 'Sans nom'}</div>
                            <div className="flex items-center gap-1">
                              <Eye className="h-4 w-4 text-slate-400" />
                              <button onClick={(e) => deleteProject(project.project_id, e)}
                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-1 transition-opacity"
                                data-testid={`delete-project-${idx}`}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="text-slate-500 text-xs mt-1">Offre max: {fmt(project.offre_maximum || 0)}</div>
                          {project.rentabilite && (
                            <div className="text-[#00B050] text-xs font-medium">Rentabilit&eacute;: {project.rentabilite.toFixed(1)}%</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MDBPage;
